import { useMemo } from "react";
import { useQuery } from "react-query";
import { Button, Flex, Text } from "@radix-ui/themes";
import { Info } from "lucide-react";

import { useReleaseConversationApi } from "@/v2/entities/release-conversation/ReleaseConversation.api";
import { Loader } from "@/components/ui/loader";
import { RegressionSetItem } from "@/v2/entities/regression/Regression.model";
import { filterActionMessages } from "./utils";
import styles from "./styles.module.scss";

interface ItemDetailViewProps {
  item: RegressionSetItem;
  onForget: () => void;
  isForgetLoading: boolean;
  onRemoveFromSet: () => void;
  isRemovingFromSet: boolean;
}

export function ItemDetailView({
  item,
  onForget,
  isForgetLoading,
  onRemoveFromSet,
  isRemovingFromSet,
}: ItemDetailViewProps) {
  const releaseConversationApi = useReleaseConversationApi();
  const isCustomQuestion = !item.releaseConversationId;

  const { data: comments, isLoading } = useQuery(
    ["release-conversation-comments", item.releaseConversationId],
    () =>
      releaseConversationApi.getReleaseConversationComments(
        item.releaseConversationId!
      ),
    { enabled: !!item.releaseConversationId }
  );

  const filteredComments = useMemo(() => {
    if (!comments) return [];
    const cleaned = filterActionMessages(comments);
    if (item.expectedDecision === "escalate" && cleaned.length > 0) {
      const lastAgentIndex = cleaned
        .map((c, i) => (c.isAgent ? i : -1))
        .filter((i) => i !== -1)
        .pop();
      if (lastAgentIndex !== undefined && lastAgentIndex !== -1) {
        return cleaned.filter((_, i) => i !== lastAgentIndex);
      }
    }
    return cleaned;
  }, [comments, item.expectedDecision]);

  return (
    <div className={styles.resultDetailContainer}>
      <div className={styles.resultDetailHeader}>
        <Text size="2" className={styles.questionTitle}>
          {item.question || "Training session"}
        </Text>
        <Flex gap="2" align="center">
          <Button
            size="1"
            variant="outline"
            color="gray"
            onClick={onRemoveFromSet}
            disabled={isRemovingFromSet}
          >
            {isRemovingFromSet ? "Removing..." : "Remove from set"}
          </Button>
          {!isCustomQuestion && (
            <Button
              size="1"
              variant="outline"
              color="red"
              onClick={onForget}
              disabled={isForgetLoading}
            >
              {isForgetLoading ? "Forgetting..." : "Forget"}
            </Button>
          )}
        </Flex>
      </div>

      <div className={styles.messageContainer}>
        {isCustomQuestion ? (
          <>
            <div className={styles.userMessage}>
              <Text size="2">{item.question}</Text>
            </div>
            {item.expectedAnswer && item.expectedDecision !== "escalate" && (
              <div className={styles.agentMessage}>
                <Text size="2">{item.expectedAnswer}</Text>
              </div>
            )}
          </>
        ) : isLoading ? (
          <div className={styles.loadingContainer}>
            <Loader />
          </div>
        ) : filteredComments.length > 0 ? (
          filteredComments.map((comment) => (
            <div
              key={comment.id}
              className={
                comment.isAgent ? styles.agentMessage : styles.userMessage
              }
            >
              <Text size="2">{comment.comment}</Text>
            </div>
          ))
        ) : (
          <Text size="2" color="gray">
            No conversation available
          </Text>
        )}
        {item.expectedDecision === "escalate" && (
          <div className={styles.escalationMessage}>
            <Info size={16} />
            <Text size="2">Quack will escalate it to an agent</Text>
          </div>
        )}
      </div>
    </div>
  );
}
